import {
  checkExhaustive,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {DefaultMap} from 'external/dev_april_corgi+/js/common/collections';

import {
  Pad,
  PipeId,
  Node,
  NodeSpec,
  VolumeConstraint,
  hashMaterial,
} from '../execution/graph';
import {REMAINDER_WELL_VOLUME_NL} from '../execution/opentrons';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {WORKING_TEMPERATURE_C} from '../types/conventions';
import {
  ArrayValue,
  DataValue,
  MaterialId,
  MaterialValue,
  PlateValue,
  PlatedMaterialValue,
  ScriptValue,
} from '../types/data';

import {ElementSelectingTransformer} from './element_selecting_transformer';
import * as starlark from './starlark';

const ALIQUOTS_PAD_ID = 'aliquots';
const WORKING_LID_TEMPERATURE_C = 105;

export const NODE = {
  id: 'node_thermocycle',
  label: 'Thermocycle',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
    {
      id: 'routine',
      label: 'Routine',
      type: {
        kind: 'script',
        canThermocycle: true,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const materials = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const routine = overlay.singleValuePadSource(
      checkExists(node.inputs.get('routine')),
      'script',
      () => ({
        kind: 'script',
        value: `
# Starlark is a simplified Python. Available functions:
# * hold_at(temperature=30, seconds=60, minutes=32, hours=1)
# * take_aliquots(suffix="cycle1")
hold_at(temperature=30, seconds=0)
        `.trim(),
      }),
    );

    const thermocycle = new ThermocyclingTransformer(
      overlay.nextIdFor(node),
      materials as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
      plate,
      routine,
    );

    overlay.setPadSource(
      checkExists(node.outputs.get('materials')),
      new ElementSelectingTransformer(
        overlay.nextIdFor(node),
        thermocycle,
        /* index= */ 0,
      ),
    );

    const aliquotsPad = node.outputs.get(ALIQUOTS_PAD_ID);
    const aliquotsPadTransformer = new AliquotsPadTransformer(
      overlay.nextIdFor(node),
      thermocycle,
      node,
    );
    if (aliquotsPad) {
      overlay.setPadSource(aliquotsPad, aliquotsPadTransformer);
    }
  },
} as const satisfies NodeSpec;

export class ThermocyclingTransformer extends PTransformer<
  DataValue,
  ArrayValue<ArrayValue<PlatedMaterialValue>>
> {
  constructor(
    id: PipeId,
    private readonly materials: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly plate: Source<PlateValue>,
    private readonly routine: Source<ScriptValue>,
  ) {
    super(id, [...materials, plate, routine]);
  }

  async collate(context: PipeContext): Promise<void> {
    const inputs: PlatedMaterialValue[] = [];
    for (const {value, source, reject} of this.values.values()) {
      if (this.materials.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          inputs.push(v);
        });
      }
    }
    const plateValue = checkExists(this.values.get(this.plate));
    const plateId = (checkExists(plateValue.value) as PlateValue).id;
    const routine = checkExists(this.values.get(this.routine));
    const routineScript = checkExists(routine.value) as ScriptValue;
    let evaluated;
    try {
      evaluated = (await starlark.ready()).evaluate(
        routineScript?.value ?? '',
        {canThermocycle: true},
      );
      if (evaluated.length === 0) {
        throw new Error("Routine doesn't specify any thermocycles");
      }
    } catch (e: unknown) {
      routine.reject(e);
      return;
    }

    const thermocycler = context.lab.plate(
      context.lab.presetPlates.thermocycling,
    );
    const onThermocycler: PlatedMaterialValue[] = [];
    for (const plated of inputs) {
      const well = thermocycler.reserveWell();
      onThermocycler.push({
        kind: 'plated_material',
        material: plated.material,
        plate: thermocycler.id,
        well: well.id,
      });
    }

    const aliquotGroups: PlatedMaterialValue[][] = [];
    for (const action of evaluated) {
      if (action.kind !== 'take_aliquots') {
        continue;
      }

      const plate = context.lab.plate(plateId);
      const group: PlatedMaterialValue[] = [];
      aliquotGroups.push(group);
      for (const plated of onThermocycler) {
        const well = plate.reserveWell();
        group.push({
          kind: 'plated_material',
          material: aliquotIntermediateMaterial(plated.material, action.suffix),
          plate: plate.id,
          well: well.id,
        });
      }
    }

    const products: PlatedMaterialValue[] = [];
    {
      const plate = context.lab.plate(plateId);
      for (const plated of onThermocycler) {
        const well = plate.reserveWell();
        products.push({
          kind: 'plated_material',
          material: thermocycleMaterial(plated.material),
          plate: plate.id,
          well: well.id,
        });
      }
    }

    const constraints = await this.gatherConstraints(
      {
        kind: 'array',
        elements: [
          {kind: 'array', elements: products},
          ...aliquotGroups.map(g => ({kind: 'array', elements: g}) as const),
        ],
      },
      context,
    );
    const arrayConstraints = constraints.array ?? [];

    let maxVolumeNl = 0;
    const used = new DefaultMap<MaterialId, VolumeConstraint>(() => ({
      numberNl: 0,
    }));
    const onMovements = [];
    for (let i = 0; i < inputs.length; ++i) {
      const needNl = sum(
        arrayConstraints.map(c => (c.array?.[i] ?? {}).volume?.numberNl ?? 0),
      );
      maxVolumeNl = Math.max(maxVolumeNl, needNl);
      if (needNl > 0) {
        const usedNl = needNl + REMAINDER_WELL_VOLUME_NL;
        const input = inputs[i];
        used.get(input.material.id).numberNl += usedNl;
        const thermo = onThermocycler[i];
        onMovements.push({from: input, to: thermo, volumeNl: usedNl});
      }
    }

    const promises = [];
    for (const {source, value, resolve, reject} of this.values.values()) {
      if (this.materials.indexOf(source as any) >= 0) {
        promises.push(
          resolve(
            walkMaterials(value, reject, v => {
              if (!v) {
                return {};
              }

              // TODO(april): if the material is provided multiple times we duplicate usage
              return {volume: used.get(v.material.id)};
            }),
          ),
        );
      } else {
        promises.push(resolve({}));
      }
    }
    await Promise.all(promises);

    if (maxVolumeNl > 0) {
      const productConstraints = arrayConstraints[0].array ?? [];
      const aliquotConstraintsGroups = arrayConstraints
        .slice(1)
        .map(c => c.array ?? []);
      const actor = context.dispatcher.actor(this);
      actor.thermocycleLidHinge(thermocycler, /* closed= */ false);
      actor.thermocycleLidTemperature(thermocycler, WORKING_LID_TEMPERATURE_C);
      actor.thermocycleBlockTemperature(thermocycler, {
        holdDurationUs: undefined,
        temperatureC: WORKING_TEMPERATURE_C,
      });

      for (const {from, to, volumeNl} of onMovements) {
        actor.pipette(
          context.lab.plate(from.plate).well(from.well),
          context.lab.plate(to.plate).well(to.well),
          {mix: false, volumeNl},
        );
      }

      actor.thermocycleLidHinge(thermocycler, /* closed= */ true);

      let aliquotIndex = 0;
      for (const action of evaluated) {
        if (action.kind === 'hold_temperature') {
          actor.thermocycleBlockTemperature(thermocycler, {
            holdDurationUs: action.durationS * 1_000_000,
            temperatureC: action.temperatureC,
          });
        } else if (action.kind === 'take_aliquots') {
          const needNls = aliquotConstraintsGroups[aliquotIndex].map(
            s => s.volume?.numberNl ?? 0,
          );
          const takingLiquid = needNls.filter(nl => nl > 0).length > 0;
          if (takingLiquid) {
            actor.thermocycleLidHinge(thermocycler, /* closed= */ false);
            for (
              let sampleIndex = 0;
              sampleIndex < products.length;
              ++sampleIndex
            ) {
              const nl = needNls[sampleIndex] ?? 0;
              if (nl > 0) {
                const thermo = onThermocycler[sampleIndex];
                const aliquot = aliquotGroups[aliquotIndex][sampleIndex];
                actor.pipette(
                  context.lab.plate(thermo.plate).well(thermo.well),
                  context.lab.plate(aliquot.plate).well(aliquot.well),
                  {mix: false, volumeNl: nl},
                );
              }
            }
            actor.thermocycleLidHinge(thermocycler, /* closed= */ true);
          }
          aliquotIndex += 1;
        } else {
          throw checkExhaustive(action);
        }
      }

      actor.thermocycleBlockTemperature(thermocycler, {
        holdDurationUs: undefined,
        temperatureC: WORKING_TEMPERATURE_C,
      });
      actor.thermocycleLidHinge(thermocycler, /* closed= */ false);
      actor.thermocycleLidDeactivate(thermocycler);

      const productNls = productConstraints.map(s => s.volume?.numberNl ?? 0);
      for (let sampleIndex = 0; sampleIndex < products.length; ++sampleIndex) {
        const thermo = onThermocycler[sampleIndex];
        const output = products[sampleIndex];
        const needNl = productNls[sampleIndex] ?? 0;
        if (needNl > 0) {
          actor.pipette(
            context.lab.plate(thermo.plate).well(thermo.well),
            context.lab.plate(output.plate).well(output.well),
            {mix: false, volumeNl: needNl},
          );
        }
      }

      actor.thermocycleBlockDeactivate(thermocycler);
    }
  }
}

class AliquotsPadTransformer extends PTransformer<
  ArrayValue<ArrayValue<PlatedMaterialValue>>,
  ArrayValue<ArrayValue<PlatedMaterialValue>>
> {
  constructor(
    id: PipeId,
    private readonly array: Source<ArrayValue<ArrayValue<PlatedMaterialValue>>>,
    private readonly node: Node,
  ) {
    super(id, [array]);
  }

  async collate(context: PipeContext): Promise<void> {
    const array = checkExists(this.values.get(this.array));
    const value = {
      kind: 'array',
      elements: array.value?.elements.slice(1) ?? [],
    } as const;
    const constraints = await this.gatherConstraints(value, context);
    await array.resolve({
      array: [
        {
          array: Array(array.value?.elements[0].elements.length).fill({
            volume: {numberNl: 0},
          }),
        },
        ...(constraints.array ??
          Array(value.elements.length).fill({volume: {numberNl: 0}})),
      ],
    });

    const existingPad = this.node.outputs.get(ALIQUOTS_PAD_ID);
    if (value.elements.length > 0 && !existingPad) {
      const pad: Pad<'output'> = {
        id: ALIQUOTS_PAD_ID,
        node: this.node,
        direction: 'output',
        label: 'Aliquots',
        type: {
          kind: 'array',
          elementsType: {
            kind: 'plated_material',
            material: undefined,
          },
        },
        links: new Map(),
      };
      context.changes.addPad(pad);
    } else if (value.elements.length === 0 && existingPad) {
      // We've already unblocked downstream nodes so we can just remove it
      await context.changes.removePad(existingPad);
    }
  }
}

function aliquotIntermediateMaterial(
  material: MaterialValue,
  suffix: string,
): MaterialValue {
  const treated: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label: material.label + '.' + suffix,
    amount: material.amount,
    contents: [material],
    tags: {treatment: 'thermocycled'},
  };
  treated.id = hashMaterial(treated);
  return treated;
}

function thermocycleMaterial(material: MaterialValue): MaterialValue {
  const treated: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label: material.label,
    amount: material.amount,
    contents: [material],
    tags: {treatment: 'thermocycled'},
  };
  treated.id = hashMaterial(treated);
  return treated;
}

function sum(numbers: number[]): number {
  let s = 0;
  for (const v of numbers) {
    s += v;
  }
  return s;
}
